/**
 * BatchRecordView — the executed batch record, as the runner recorded it (Execution revamp step 2, EXV-019/020).
 * =================================================================================
 * Fed by GET /bp/batch/record-view (services/execution/record_view): the same source the certified copy reads.
 * Per operation a card: its steps, each with its recorded values (name first, value and unit, limit, verdict,
 * who / when), corrections inline (old → new, reason, who, when), voided entries with their reason, the signatures
 * given on the step, and the operation's deviations as chips. Then the batch's signature register: signer · role ·
 * meaning · time · record id, exactly as recorded.
 *
 * The client decides nothing: every word is the server's; the only mapping is a verdict word to an icon state.
 * Internal ids sit under a closed "Details". Cards stack under 768 px.
 *
 * Also exports OperationRecordCard (one operation's card, used by the operation drill-down).
 *
 * `layoutSwitch` (Review by Exception only) adds a Cards | Table switch and shows the operations as one grouped table
 * (BatchRecordTable) by default; the viewer's choice is kept in localStorage. Without it the view is cards, unchanged.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Card, Empty, Grid, Segmented, Space, Spin, Tag, Tooltip, theme } from 'antd';
import { RefreshCw } from 'lucide-react';
import StatusIndicator from '@/components/genealogy/StatusIndicator';
import { formatDateTime } from '@/utils/datetime';
import { get_batch_record_view } from '@/utils/api';
import { statusState } from '@/pages/Batch-Processing/Recipe/BatchProcessDetails/helpers';
import BatchRecordTable from '@/components/batch/BatchRecordTable';

const LAYOUT_KEY = 'batchRecord.layout';
const readLayout = () => {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'cards' ? 'cards' : 'table';
  } catch {
    return 'table';
  }
};
const writeLayout = (v) => {
  try {
    localStorage.setItem(LAYOUT_KEY, v);
  } catch {
    // storage unavailable (private window, blocked site data): the choice lasts for this view only
  }
};

const fmt = (v) => (v ? formatDateTime(v) : '—');

/** A verdict word (server's) → an icon state. Anything not listed stays neutral, shown as written. */
const VERDICT_STATE = { 'in range': 'ok', 'out of range': 'warning' };
const verdictState = (word) => VERDICT_STATE[String(word || '').trim().toLowerCase()] || 'neutral';

const joinDot = (parts) => parts.filter(Boolean).join(' · ');

const Details = ({ rows }) => {
  const { token } = theme.useToken();
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
  if (!shown.length) return null;
  return (
    <details style={{ marginTop: 4, fontSize: 11.5, color: token.colorTextTertiary }}>
      <summary style={{ cursor: 'pointer', width: 'fit-content' }}>Details</summary>
      <div style={{ paddingTop: 2 }}>
        {shown.map(([k, v]) => (
          <div key={k}>
            {k}: {Array.isArray(v) ? v.join(', ') : String(v)}
          </div>
        ))}
      </div>
    </details>
  );
};
Details.propTypes = { rows: PropTypes.array.isRequired };

const ValueRow = ({ v }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ padding: '6px 0', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '2px 12px' }}>
        <span style={{ flex: '1 1 220px', minWidth: 0, color: token.colorText, fontSize: 13, overflowWrap: 'anywhere' }}>
          {v.label || '—'}
        </span>
        <span style={{ fontWeight: 600, color: token.colorText, fontSize: 13 }}>
          {v.value === '' ? '—' : v.value}
          {v.unit ? ` ${v.unit}` : ''}
        </span>
        {v.limit ? <span style={{ fontSize: 12, color: token.colorTextSecondary }}>limit {v.limit}</span> : null}
        {v.verdict ? <StatusIndicator state={verdictState(v.verdict)} label={v.verdict} /> : null}
        {v.capture ? (
          <Tooltip title={joinDot([v.capture.tag, v.capture.source_at ? fmt(v.capture.source_at) : null, v.capture.quality])}>
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>Captured</Tag>
          </Tooltip>
        ) : null}
        <span style={{ fontSize: 12, color: token.colorTextSecondary }}>{joinDot([v.recorded_by, v.recorded_at ? fmt(v.recorded_at) : null])}</span>
      </div>
      {(v.corrections || []).map((c, i) => (
        <div key={i} style={{ fontSize: 12, color: token.colorTextSecondary, paddingLeft: 12, overflowWrap: 'anywhere' }}>
          Corrected {c.old || '—'} → {c.new || '—'}
          {c.reason ? ` — ${c.reason}` : ''}
          {c.by || c.at ? ` (${joinDot([c.by, c.at ? fmt(c.at) : null])})` : ''}
        </div>
      ))}
      <Details rows={[['Entries', v.runner_entries], ['Entry', v.entry_id], ['Step id', v.instance_id]]} />
    </div>
  );
};
ValueRow.propTypes = { v: PropTypes.object.isRequired };

const SignatureLine = ({ s }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ fontSize: 12.5, color: token.colorText, padding: '3px 0', overflowWrap: 'anywhere' }}>
      <StatusIndicator
        state={s.standing ? 'ok' : 'error'}
        label={joinDot([s.name, s.role, s.meaning, s.signed_at ? fmt(s.signed_at) : null])}
        tooltip={s.standing ? undefined : s.validity}
      />
      {!s.standing && s.validity ? <span style={{ color: token.colorTextSecondary }}> — {s.validity}</span> : null}
      {s.signature_record_id ? <span style={{ color: token.colorTextSecondary }}> · record {s.signature_record_id}</span> : null}
    </div>
  );
};
SignatureLine.propTypes = { s: PropTypes.object.isRequired };

const DeviationChips = ({ deviations }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {deviations.map((d, i) => (
      <Tooltip
        key={i}
        title={joinDot([d.parameter, d.actual ? `actual ${d.actual}` : null, d.limit ? `limit ${d.limit}` : null, d.status, d.action, d.raised_by, d.when])}
      >
        <Tag color="warning" style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
          {joinDot([d.type, d.parameter])}
        </Tag>
      </Tooltip>
    ))}
  </div>
);
DeviationChips.propTypes = { deviations: PropTypes.array.isRequired };

/** One operation's card: its steps with values, corrections, voids and signatures; its deviations as chips. */
export const OperationRecordCard = ({ op, focusStep = null }) => {
  const { token } = theme.useToken();
  const counts = op.counts || {};
  const steps = op.steps || [];
  const readings = op.readings || [];
  const worked = (op.worked_by || []).join(', ') || op.started_by;
  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', whiteSpace: 'normal' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: token.colorText }}>
            {op.operation_number != null ? `${op.operation_number}. ` : ''}
            {op.operation_name || '—'}
          </span>
          {op.status ? <StatusIndicator state={statusState(op.status)} label={op.status} /> : null}
        </div>
      }
    >
      <div style={{ fontSize: 12, color: token.colorTextSecondary, display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginBottom: 6 }}>
        {op.process_name ? <span>{op.process_name}</span> : null}
        <span>{fmt(op.started_at)} → {op.ended_at ? fmt(op.ended_at) : 'not ended'}</span>
        {worked ? <span>Worked by {worked}</span> : null}
        {op.ended_by ? <span>Completed by {op.ended_by}</span> : null}
        {op.machine ? <span>Machine {op.machine}</span> : null}
        <span>
          {counts.values ?? 0} value{counts.values === 1 ? '' : 's'} · {counts.signatures ?? 0} signature{counts.signatures === 1 ? '' : 's'}
          {counts.corrections ? ` · ${counts.corrections} correction${counts.corrections === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      {(op.deviations || []).length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <DeviationChips deviations={op.deviations} />
        </div>
      )}
      {steps.length === 0 && readings.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing recorded on this operation" />
      ) : null}
      {steps.map((st) => (
        <div
          key={st.instance_id}
          id={`record-step-${st.instance_id}`}
          data-focused={focusStep === st.instance_id ? 'true' : undefined}
          style={{
            marginTop: 8, padding: '6px 10px', borderRadius: token.borderRadius, background: token.colorFillQuaternary,
            outline: focusStep === st.instance_id ? `2px solid ${token.colorPrimary}` : undefined,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, color: token.colorText, overflowWrap: 'anywhere' }}>{st.title || '—'}</div>
          {st.values.length === 0 && st.signatures.length === 0 && st.voided.length === 0 ? (
            <div style={{ fontSize: 12, color: token.colorTextTertiary }}>Nothing recorded on this step</div>
          ) : null}
          {st.values.map((v, i) => <ValueRow key={(v.runner_entries || [])[0] ?? i} v={v} />)}
          {st.voided.map((x, i) => (
            <div key={i} style={{ fontSize: 12, color: token.colorTextSecondary, padding: '4px 0', overflowWrap: 'anywhere' }}>
              Voided: {x.label || '—'} {x.value ? `(${x.value})` : ''}
              {x.reason ? ` — ${x.reason}` : ''}
              {x.by || x.at ? ` (${joinDot([x.by, x.at ? fmt(x.at) : null])})` : ''}
            </div>
          ))}
          {st.signatures.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {st.signatures.map((s, i) => <SignatureLine key={s.signature_record_id ?? i} s={s} />)}
            </div>
          )}
          <Details rows={[['Step id', st.instance_id], ['Step code', st.node_code]]} />
        </div>
      ))}
      {readings.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: token.colorText }}>Readings</div>
          {readings.map((v, i) => <ValueRow key={v.entry_id ?? i} v={v} />)}
        </div>
      )}
      <Details rows={[['Operation id', op.operation_id], ['Batch operation id', op.batch_config_id]]} />
    </Card>
  );
};
OperationRecordCard.propTypes = { op: PropTypes.object.isRequired, focusStep: PropTypes.number };

const SIGNATURE_COLUMNS = ['Signer', 'Role', 'Meaning', 'Signed', 'On', 'Record'];

/** The batch's signature register: every signature on the record, step and batch level, as recorded. */
export const SignatureRegister = ({ signatures }) => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const narrow = !screens.md;
  if (!signatures.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No signatures on this record" />;
  }
  const on = (s) => (s.scope === 'step' ? joinDot([s.operation_name, s.step]) : s.document);
  if (narrow) {
    return (
      <div>
        {signatures.map((s, i) => (
          <div key={s.signature_record_id ?? i} style={{ padding: '8px 0', borderTop: i ? `1px solid ${token.colorBorderSecondary}` : 'none' }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: token.colorText }}>
              {s.name || '—'}
              {s.role ? <span style={{ fontWeight: 400, color: token.colorTextSecondary }}> · {s.role}</span> : null}
            </div>
            <div style={{ fontSize: 12.5, color: token.colorText }}>
              <StatusIndicator state={s.standing ? 'ok' : 'error'} label={s.meaning || '—'} tooltip={s.standing ? undefined : s.validity} />
            </div>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, overflowWrap: 'anywhere' }}>
              {joinDot([fmt(s.signed_at), on(s), s.signature_record_id ? `record ${s.signature_record_id}` : null])}
            </div>
            {!s.standing && s.validity ? <div style={{ fontSize: 12, color: token.colorTextSecondary }}>{s.validity}</div> : null}
          </div>
        ))}
      </div>
    );
  }
  const cell = { padding: '6px 8px', borderTop: `1px solid ${token.colorBorderSecondary}`, verticalAlign: 'top', color: token.colorText };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr>
            {SIGNATURE_COLUMNS.map((h) => (
              <th key={h} style={{ ...cell, borderTop: 'none', textAlign: 'left', fontWeight: 600, color: token.colorTextSecondary }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {signatures.map((s, i) => (
            <tr key={s.signature_record_id ?? i}>
              <td style={cell}>{s.name || '—'}</td>
              <td style={cell}>{s.role || '—'}</td>
              <td style={cell}>
                <StatusIndicator state={s.standing ? 'ok' : 'error'} label={s.meaning || '—'} tooltip={s.standing ? undefined : s.validity} />
                {!s.standing && s.validity ? <div style={{ color: token.colorTextSecondary }}>{s.validity}</div> : null}
              </td>
              <td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmt(s.signed_at)}</td>
              <td style={cell}>{on(s) || '—'}</td>
              <td style={cell}>{s.signature_record_id ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
SignatureRegister.propTypes = { signatures: PropTypes.array.isRequired };

/** The batch record view: operation cards, then the signature register. */
const BatchRecordView = ({ batchNumber, focusStep = null, layoutSwitch = false }) => {
  const { token } = theme.useToken();
  const [layout, setLayout] = useState(() => (layoutSwitch ? readLayout() : 'cards'));
  const asTable = layoutSwitch && layout === 'table';
  const changeLayout = (v) => {
    setLayout(v);
    writeLayout(v);
  };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!batchNumber) return;
    setLoading(true);
    get_batch_record_view({ batch_number: batchNumber }, (res) => {
      setLoading(false);
      if (res?.error) {
        setError({ status: res.status, message: res.message });
        setData(null);
        return;
      }
      setError(null);
      setData(res?.data || null);
    });
  }, [batchNumber]);

  useEffect(() => {
    load();
  }, [load]);

  // a caller may name one step to bring into view (the review page's "Show in batch record")
  useEffect(() => {
    if (!data || focusStep == null || asTable) return;
    const el = document.getElementById(`record-step-${focusStep}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [data, focusStep, asTable]);

  if (loading && !data) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin tip="Loading the batch record…" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type={error.status === 404 ? 'warning' : 'error'}
        showIcon
        message={error.status === 404 ? 'Batch not found in this plant' : 'Could not load the batch record'}
        description={error.message}
        action={<Button size="small" icon={<RefreshCw size={13} />} onClick={load}>Retry</Button>}
      />
    );
  }
  if (!data) return null;
  const t = data.totals || {};
  return (
    <div data-testid="batch-record-view">
      <Card
        size="small"
        style={{ marginTop: 12 }}
        styles={{ body: { padding: 8 } }}
        title={<span style={{ fontSize: 13 }}>Executed record</span>}
        extra={(
          <Space size={8} wrap>
            {layoutSwitch ? (
              <Segmented
                size="small"
                value={layout}
                onChange={changeLayout}
                options={[{ label: 'Cards', value: 'cards' }, { label: 'Table', value: 'table' }]}
                data-testid="record-layout"
              />
            ) : null}
            <Button size="small" icon={<RefreshCw size={13} />} onClick={load} loading={loading}>Refresh record</Button>
          </Space>
        )}
      >
        <div style={{ fontSize: 12.5, color: token.colorTextSecondary, marginBottom: 8 }}>
          {joinDot([
            `${t.operations ?? 0} operation${t.operations === 1 ? '' : 's'}`,
            `${t.values ?? 0} recorded value${t.values === 1 ? '' : 's'}`,
            `${t.signatures ?? 0} signature${t.signatures === 1 ? '' : 's'}`,
            t.corrections ? `${t.corrections} correction${t.corrections === 1 ? '' : 's'}` : null,
            `${t.deviations ?? 0} deviation${t.deviations === 1 ? '' : 's'}`,
          ])}
        </div>
        {(data.batch_level_deviations || []).length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, color: token.colorText, marginBottom: 4 }}>Recorded against the batch, not one operation</div>
            <DeviationChips deviations={data.batch_level_deviations} />
          </div>
        )}
        {(data.operations || []).length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No operations recorded for this batch" />
        ) : asTable ? (
          <BatchRecordTable operations={data.operations} focusStep={focusStep} />
        ) : (
          data.operations.map((op) => <OperationRecordCard key={op.batch_config_id} op={op} focusStep={focusStep} />)
        )}
      </Card>
      <Card
        size="small"
        style={{ marginTop: 12 }}
        styles={{ body: { padding: 8 } }}
        title={<span style={{ fontSize: 13 }}>Signatures ({(data.signatures || []).length})</span>}
      >
        <SignatureRegister signatures={data.signatures || []} />
      </Card>
    </div>
  );
};

BatchRecordView.propTypes = { batchNumber: PropTypes.string, focusStep: PropTypes.number, layoutSwitch: PropTypes.bool };

export default BatchRecordView;
